import VoiceChatStatusBadge from 'components/badge/VoiceChatStatusBadge';
import IndicatorScreen from 'components/indicator/IndicatorScreen';
import { DivConst } from 'constants/divConst';
import { useNavigate, useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { useAtom } from 'jotai';
import { AcctState } from 'stores/atoms/acctState';
import WsModalReportDownload from 'pages/ws/components/modalReportDownload/WsModalReportDownload';
import WsModalReportTerm from 'pages/ws/components/modalReportTerm/WsModalReportTerm';
import WsReportChart from 'pages/ws/components/reportChart/WsReportChart';
import WsReportDetailHeader from 'pages/ws/components/reportDetailHeader/WsReportDetailHeader';
import WsReportTermSection from 'pages/ws/components/reportTermSection/WsReportTermSection';
import WsReportMetrics from 'pages/ws/components/reportMetrics/WsReportMetrics';
import WsReportVoiceChatFunnel from 'pages/ws/reportVoiceChat/components/reportVoiceChatFunnel/WsReportVoiceChatFunnel';
import WsReportVoiceChatTabs from 'pages/ws/reportVoiceChat/components/reportVoiceChatTabs/WsReportVoiceChatTabs';
import { useWsReportVoiceChat } from 'pages/ws/reportVoiceChat/hooks/useWsReportVoiceChat';

import type { JSX } from 'react';

/**
 * ボイスチャットレポート詳細画面（WsReportForm ミラー）。
 * ヘッダ/期間/KPI/チャートは本物 presentational を流用し、音声固有 2 タブを追加する。
 */
export default function WsReportVoiceChat(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const navigate = useNavigate();
  const { voiceChatCd } = useParams();

  const [acctState] = useAtom(AcctState);

  const {
    initialized,
    processing,
    report,
    respondents,
    viewers,
    viewerFilterDiv,
    onChangeViewerFilter,
    onDownloadViewerCsv,
    reportDateFrom,
    reportDateTo,
    isExpiredPlan,
    modalTermOpen,
    setModalTermOpen,
    modalDownloadOpen,
    setModalDownloadOpen,
    onContentOpen,
    onModalReportDownloadOpen,
    onModalReportTermOpen,
    onChangeReportTerm,
  } = useWsReportVoiceChat(voiceChatCd, acctState.company);

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {/* 初期化中の表示 */}
      <IndicatorScreen visible={!initialized || processing} />
      {/* 初期化後の表示 */}
      {initialized && report && (
        <>
          <WsReportDetailHeader
            imageKey={report.imageKey}
            title={report.title}
            createdAt={report.createdAt}
            statusBadge={<VoiceChatStatusBadge voiceChatStatusDiv={report.voiceChatStatusDiv} />}
            isExpiredPlan={isExpiredPlan}
            onBack={() => navigate(-1)}
            onContentOpen={onContentOpen}
            onModalReportDownloadOpen={onModalReportDownloadOpen}
          />

          <WsReportTermSection
            reportDateFrom={reportDateFrom}
            reportDateTo={reportDateTo}
            onModalReportTermOpen={onModalReportTermOpen}
          />

          <WsReportMetrics
            participatedCount={report.participatedCount}
            lineFlag={report.lineFlag}
            mailAddressCount={report.mailAddressCount}
            lineCount={report.lineCount}
            mailAddressAcquisitionRate={report.mailAddressAcquisitionRate}
            lineAddressAcquisitionRate={report.lineAddressAcquisitionRate}
            answeredCount={report.answeredCount}
            answeredRate={report.answeredRate}
          />

          {/* 視聴ファネル（パラメータモード ON のみ表示） */}
          <WsReportVoiceChatFunnel viewingFunnel={report.viewingFunnel} />

          {/* 音声レポート本体（設問ごと／回答者ごと／視聴者一覧） */}
          <WsReportVoiceChatTabs
            questions={report.questions}
            respondents={respondents}
            viewers={viewers}
            viewerFilterDiv={viewerFilterDiv}
            onChangeViewerFilter={onChangeViewerFilter}
            onDownloadViewerCsv={onDownloadViewerCsv}
          />

          {/* レポートチャート */}
          <WsReportChart reports={report.reports} lineFlag={report.lineFlag} />
        </>
      )}

      {/* レポート期間設定モーダル */}
      <WsModalReportTerm
        open={modalTermOpen}
        setOpen={setModalTermOpen}
        reportDateFrom={reportDateFrom}
        reportDateTo={reportDateTo}
        onSubmit={attr => onChangeReportTerm(attr.reportDateFrom, attr.reportDateTo)}
      />
      {/* レポートダウンロードモーダル */}
      <WsModalReportDownload
        open={modalDownloadOpen}
        setOpen={setModalDownloadOpen}
        cd={voiceChatCd}
        contentsType={DivConst.items.contentsType.voiceChat.value}
        reportDateFrom={null}
        reportDateTo={null}
      />
      {
        // プラン（利用期間切れ）の場合に表示する
        isExpiredPlan && <Tooltip id="custom-tooltip" place="top" variant="info" />
      }
    </>
  );
}
